import { ThemeNames } from '@modernloop/shared/theme';
import { LocalStorageUtil } from '@modernloop/shared/utils';

const STORAGE_KEY = 'MLOOP_DASHBOARD_COLOR_SCHEME';

export type ColorScheme = ThemeNames.LIGHT | ThemeNames.DARK;
export const SYSTEM_COLOR_SCHEME = 'SYSTEM' as const;
export type ColorSchemePreferenceValue = ColorScheme | typeof SYSTEM_COLOR_SCHEME;

const isColorSchemePreference = (value: unknown): value is ColorSchemePreferenceValue =>
  value === ThemeNames.LIGHT || value === ThemeNames.DARK || value === SYSTEM_COLOR_SCHEME;

const isStorageAvailable = (): boolean => {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
};

/**
 * Parses and persists the hiring dashboard color scheme at the localStorage boundary.
 */
export const ColorSchemePreference = {
  DEFAULT: ThemeNames.LIGHT as ColorSchemePreferenceValue,

  parse(value: unknown): ColorSchemePreferenceValue | null {
    return isColorSchemePreference(value) ? value : null;
  },

  fromStorage(): ColorSchemePreferenceValue {
    if (!isStorageAvailable()) {
      return ColorSchemePreference.DEFAULT;
    }

    try {
      return ColorSchemePreference.parse(LocalStorageUtil.get(STORAGE_KEY)) ?? ColorSchemePreference.DEFAULT;
    } catch {
      return ColorSchemePreference.DEFAULT;
    }
  },

  persist(colorScheme: ColorSchemePreferenceValue): void {
    if (!isStorageAvailable()) {
      return;
    }

    try {
      LocalStorageUtil.set(STORAGE_KEY, colorScheme);
    } catch {
      // Preference cannot be persisted in this environment; in-memory state still applies.
    }
  },
} as const;
